import React, { useEffect, useState, useCallback } from 'react';
import { Smartphone, Monitor } from 'lucide-react';
import './ViewportToggle.css';

const STORAGE_KEY = 'cadence_viewport_mode';
const DESKTOP_WIDTH = 1280;
const MOBILE_PREVIEW_WIDTH = 540;
const META_SELECTOR = 'meta[name="viewport"]';

const setViewport = (mode) => {
  let meta = document.querySelector(META_SELECTOR);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'viewport');
    document.head.appendChild(meta);
  }
  if (mode === 'desktop') {
    meta.setAttribute(
      'content',
      `width=${DESKTOP_WIDTH}, initial-scale=${
        Math.round((window.screen.width / DESKTOP_WIDTH) * 100) / 100
      }, viewport-fit=cover`
    );
  } else {
    meta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=5'
    );
  }
};

// Desktop browsers IGNORE the viewport meta tag — so to make "Mobile view"
// actually visible on a real computer we mirror the device-frame approach:
// flip an attribute on <html> that CSS uses to clamp .App to 540px and add a
// faux phone bezel. Cleared when the user toggles back to Desktop.
const setDesktopFrame = (mode) => {
  const root = document.documentElement;
  if (mode === 'mobile') {
    root.setAttribute('data-cadence-frame', 'mobile');
  } else {
    root.removeAttribute('data-cadence-frame');
  }
};

const ViewportToggle = () => {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'auto';
    } catch {
      return 'auto';
    }
  });

  // Apply on mount + whenever mode changes (only when user has explicitly chosen)
  useEffect(() => {
    if (mode === 'desktop' || mode === 'mobile') {
      setViewport(mode);
      setDesktopFrame(mode);
    } else {
      setDesktopFrame('desktop');
    }
  }, [mode]);

  const toggle = useCallback(() => {
    const next =
      mode === 'desktop'
        ? 'mobile'
        : mode === 'mobile'
        ? 'desktop'
        : // First-time tap: flip to the OPPOSITE of what the device naturally renders.
        window.innerWidth < 900
        ? 'desktop'
        : 'mobile';
    setMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, [mode]);

  // Show "switch to desktop" copy when currently in mobile (or auto-on-small-screen)
  const isDesktopMode = mode === 'desktop';
  const Icon = isDesktopMode ? Smartphone : Monitor;
  const label = isDesktopMode ? 'Mobile view' : 'Desktop view';

  return (
    <button
      type="button"
      className="viewport-toggle"
      onClick={toggle}
      data-testid="viewport-toggle"
      aria-label={`Switch to ${label}`}
      title={`Switch to ${label}`}
    >
      <Icon size={16} aria-hidden="true" />
      <span className="viewport-toggle-label">{label}</span>
    </button>
  );
};

export { MOBILE_PREVIEW_WIDTH };
export default ViewportToggle;
